import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import HomePage from './pages/HomePage'
import ResearchPage from './pages/ResearchPage'
import SavedPage from './pages/SavedPage'
import ProfilePage from './pages/ProfilePage'
import LoginPage from './pages/LoginPage'
import LandingPage, { getPendingResearch, clearPendingResearch } from './pages/LandingPage'
import SharedArticlePage from './pages/SharedArticlePage'
import ReportViewPage from './pages/ReportViewPage'
import BottomNavigation from './components/shared/BottomNavigation'
import AudioPlayer from './components/shared/AudioPlayer'
import ToastContainer from './components/shared/ToastContainer'
import UserAvatar from './components/profile/UserAvatar'
import IntelligenceReportCockpit, {
  buildIntelligenceReportStudy,
  normalizeIntelligenceReportData,
  type IntelligenceReportData,
  type IntelligenceReportStudy,
} from './components/cockpit/IntelligenceReportCockpit'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { getViewMode, useScrollNavigation } from './lib/utils'
import { resolveArticleByToken, mintArticleToken, mintPublicArticleToken, isPublicArticleToken, getAuthHeader } from './lib/api'
import { LoadingAnimation } from './components/ui/LoadingAnimation'
import { ViewMode, AudioState, ToastMessage, ThemeMode } from './types'
import { clearAllCache, clearUserCache } from './lib/clearUserCache'
import { getBillingStatus, hasBillingIssueStatus, BillingStatus } from './lib/billingApi'
import { analytics } from './lib/analytics'
import { useOnlineStatus } from './hooks/useOnlineStatus'
import OfflinePage from './components/shared/OfflinePage'
import LegalPage from './pages/LegalPage'
import Footer from './components/shared/Footer'
import { resolveEmailAppOpenUrl, tryOpenInApp } from './lib/tryOpenInApp'
import { API_BASE_URL } from './lib/apiBaseUrl'

// Dev-only: lazy-load benchmark page (hoisted to module scope to avoid remounts)
const BenchmarkPage = React.lazy(() => import('./pages/BenchmarkPage'))
const ExecutiveAnalyticsPage = React.lazy(() => import('./pages/ExecutiveAnalyticsPage'))
const CANONICAL_ARTICLE_PATH_REGEX = /^\/article\/(\d+)$/
const CANONICAL_ARTICLE_TOKEN_HASH_REGEX = /^\/article\/([A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+)$/
const CANONICAL_ARTICLE_ID_HASH_REGEX = /^\/article\/(\d+)$/
const CANONICAL_REPORT_PATH_REGEX = /^\/report\/([a-zA-Z0-9_-]+)$/
const INTERNAL_EXECUTIVE_PATH_REGEX = /^\/internal\/executive\/?$/
const PREAUTH_LOGIN_HASH = '#/login'
const APP_TAB_HASHES = ['research', 'explore', 'saved', 'profile'] as const
const RESEARCH_PATH_REGEX = /^\/research\/?$/

type AppTab = typeof APP_TAB_HASHES[number]

type CanonicalArticleGateState = {
  reason: 'auth_required' | 'access_denied'
  message: string
}

type UnauthScreenState = 'preauth' | 'login'
type UnauthScreenUpdateOptions = {
  history?: 'push' | 'replace'
  url?: string
}

type InternalPageRoute = 'executive' | null

function isCanonicalArticleLocation(pathname: string, hash: string): boolean {
  return CANONICAL_ARTICLE_PATH_REGEX.test(pathname) ||
    CANONICAL_ARTICLE_TOKEN_HASH_REGEX.test(hash) ||
    CANONICAL_ARTICLE_ID_HASH_REGEX.test(hash)
}

function getPathTab(pathname: string): AppTab | null {
  return RESEARCH_PATH_REGEX.test(pathname) ? 'research' : null
}

function getInternalPageRoute(pathname: string): InternalPageRoute {
  return INTERNAL_EXECUTIVE_PATH_REGEX.test(pathname) ? 'executive' : null
}

function getHistoryStateObject(historyState: unknown): Record<string, unknown> {
  return historyState && typeof historyState === 'object'
    ? historyState as Record<string, unknown>
    : {}
}

function getUnauthScreenState(historyState: unknown): UnauthScreenState | null {
  const screen = getHistoryStateObject(historyState).unauthScreen
  return screen === 'preauth' || screen === 'login' ? screen : null
}

function buildUnauthHistoryState(
  screen: UnauthScreenState,
  historyState: unknown
): Record<string, unknown> {
  return {
    ...getHistoryStateObject(historyState),
    unauthScreen: screen,
  }
}

function getCurrentRelativeUrl(): string {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`
}

function isAppTabHash(hash: string): hash is typeof APP_TAB_HASHES[number] {
  return APP_TAB_HASHES.includes(hash as typeof APP_TAB_HASHES[number])
}

function getTabFromLocation(pathname: string, hash: string): AppTab | null {
  const pathTab = getPathTab(pathname)
  if (pathTab) {
    return pathTab
  }

  if (isAppTabHash(hash)) {
    return hash
  }

  return null
}

function getTabUrl(tab: AppTab): string {
  return tab === 'research' ? '/research' : `/#${tab}`
}

function getTabFromRelativeUrl(url: string): AppTab | null {
  try {
    const parsed = new URL(url, window.location.origin)
    return getTabFromLocation(parsed.pathname, parsed.hash.slice(1))
  } catch {
    return null
  }
}

// Make cache clearing available globally for debugging
if (typeof window !== 'undefined') {
  (window as any).clearAllCache = clearAllCache;
  (window as any).clearUserCache = clearUserCache;
}

// Helper function to get initial theme from localStorage synchronously
const getInitialTheme = (): ThemeMode => {
  if (typeof window === 'undefined') return 'light';
  try {
    const savedTheme = localStorage.getItem('insightatlas-theme') as ThemeMode;
    if (savedTheme === 'light' || savedTheme === 'dark') {
      return savedTheme;
    }
  } catch (e) {
    // localStorage not available
  }
  return 'light'; // Default to light mode
};

// Main App Content Component (needs to be inside AuthProvider)
function AppContent() {
  const { isAuthenticated, isLoading, user } = useAuth()
  const { isOnline, isChecking, checkConnection } = useOnlineStatus()
  const [viewMode, setViewMode] = useState<ViewMode>('mobile')
  const [themeMode, setThemeMode] = useState<ThemeMode>(getInitialTheme)
  // Initialize activeTab from either the canonical path route or the existing hash route.
  const getInitialTab = () => {
    return getTabFromLocation(window.location.pathname, window.location.hash.slice(1)) ?? 'explore'
  }
  const [activeTab, setActiveTab] = useState<AppTab>(getInitialTab())
  // const [isArticleOpen, setIsArticleOpen] = useState(false) // Unused - may be needed later
  // const [showResearchModal, setShowResearchModal] = useState(false) // Unused - may be needed later
  // const [hideBottomNav, setHideBottomNav] = useState(false) // Unused - may be needed later
  const [showLandingPage, setShowLandingPage] = useState(
    () => window.location.hash !== PREAUTH_LOGIN_HASH && getInternalPageRoute(window.location.pathname) === null
  )

  // Dev-only: detect benchmark hash on initial load (checked once, not every render)
  const [showBenchmark] = useState(() => 
    window.location.hash === '#benchmark' && process.env.NODE_ENV === 'development'
  )

  // Legal pages: detect #/terms or #/privacy on load
  const [legalPage, setLegalPage] = useState<'terms' | 'privacy' | null>(() => {
    const hash = window.location.hash
    if (hash === '#/terms') return 'terms'
    if (hash === '#/privacy') return 'privacy'
    return null
  })

  // Reset to show landing page when user logs out
  useEffect(() => {
    if (!isAuthenticated && !isLoading) {
      setShowLandingPage(
        window.location.hash !== PREAUTH_LOGIN_HASH &&
        getInternalPageRoute(window.location.pathname) === null
      )
    }
  }, [isAuthenticated, isLoading])

  useEffect(() => {
    let cancelled = false
    const fetchBillingIssue = async () => {
      if (!isAuthenticated) {
        setHasBillingIssue(false)
        return
      }
      try {
        const status = await getBillingStatus()
        if (!cancelled) {
          setHasBillingIssue(hasBillingIssueStatus(status))
        }
      } catch {
        if (!cancelled) {
          setHasBillingIssue(false)
        }
      }
    }
    fetchBillingIssue()
    return () => { cancelled = true }
  }, [isAuthenticated])

  // const [toastMessages, setToastMessages] = useState<ToastMessage[]>([]) // Replaced by toasts state
  const [audioState, setAudioState] = useState<AudioState>({
    isPlaying: false,
    currentTime: 0,
    duration: 0
  })
  const [toasts, setToasts] = useState<ToastMessage[]>([])
  const [profileUserEmail, setProfileUserEmail] = useState<string | undefined>(undefined)
  const [selectedProfileResearch, setSelectedProfileResearch] = useState<IntelligenceReportStudy | null>(null)
  // State for intelligence reports accessed from Profile page
  const [profileIntelligenceReportData, setProfileIntelligenceReportData] = useState<IntelligenceReportData | null>(null)
  const [isLoadingProfileReport, setIsLoadingProfileReport] = useState(false)
  const [shareId, setShareId] = useState<string | null>(() => {
    const m = window.location.pathname.match(/^\/s\/([a-zA-Z0-9]+)$/)
    return m ? m[1] : null
  })
  const [deepLinkReportId, setDeepLinkReportId] = useState<string | null>(() => {
    const m = window.location.pathname.match(CANONICAL_REPORT_PATH_REGEX)
    return m ? m[1] : null
  })
  const [internalPage, setInternalPage] = useState<InternalPageRoute>(() => getInternalPageRoute(window.location.pathname))
  const [articleToken, setArticleToken] = useState<string | null>(null)
  const [canonicalArticleGate, setCanonicalArticleGate] = useState<CanonicalArticleGateState | null>(null)
  const [resolvedArticle, setResolvedArticle] = useState<any | null>(null)
  const [resolvingArticle, setResolvingArticle] = useState<boolean>(false)
  const [resolveError, setResolveError] = useState<string | null>(null)
  const [hasBillingIssue, setHasBillingIssue] = useState(false)
  const [deepLinkResearchId, setDeepLinkResearchId] = useState<number | null>(null)
  const [deepLinkLoading, setDeepLinkLoading] = useState<boolean>(false)
  const canonicalArticleAttemptKeyRef = useRef<string | null>(null)
  const emailAppOpenAttemptKeyRef = useRef<string | null>(null)
  // Note: Universal links handle mobile deep linking automatically via AASA.
  // No custom scheme redirect needed - iOS intercepts https:// URLs before the page loads.

  useEffect(() => {
    const handlePodcastPlayback = (
      event: Event
    ) => {
      const customEvent = event as CustomEvent<{ topic?: AudioState['topic']; audio_url?: string }>
      const topic = customEvent.detail?.topic
      const audioUrl = customEvent.detail?.audio_url
      if (!topic || !audioUrl) return

      setAudioState({
        isPlaying: true,
        currentTime: 0,
        duration: topic.duration_seconds || 0,
        topic: {
          ...topic,
          audio_url: audioUrl,
        },
        status: 'ready',
      })
    }

    window.addEventListener('insightatlas-play-podcast', handlePodcastPlayback as EventListener)
    return () => {
      window.removeEventListener('insightatlas-play-podcast', handlePodcastPlayback as EventListener)
    }
  }, [])

  // Use scroll navigation hook for auto-hiding navigation
  const isNavVisible = useScrollNavigation()

  // Memoize callbacks to prevent unnecessary re-renders
  const handleResize = useCallback(() => {
    setViewMode(getViewMode())
  }, [])

  const toggleTheme = useCallback(() => {
    setThemeMode(prev => prev === 'dark' ? 'light' : 'dark')
  }, [])

  const handleBillingStatusSync = useCallback((status: BillingStatus | null) => {
    setHasBillingIssue(status ? hasBillingIssueStatus(status) : false)
  }, [])

  const addToast = useCallback((toast: Omit<ToastMessage, 'id'>) => {
    const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    const newToast = { ...toast, id }
    setToasts(prev => [...prev, newToast])
    
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id))
    }, toast.duration || 3000)
  }, [])

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  const syncUnauthHistoryState = useCallback((screen: UnauthScreenState, url = getCurrentRelativeUrl()) => {
    if (isAuthenticated) return

    const currentUrl = getCurrentRelativeUrl()
    const currentScreen = getUnauthScreenState(window.history.state)
    if (currentScreen === screen && currentUrl === url) {
      return
    }

    window.history.replaceState(
      buildUnauthHistoryState(screen, window.history.state),
      '',
      url
    )
  }, [isAuthenticated])

  const setUnauthScreen = useCallback((
    screen: UnauthScreenState,
    { history = 'replace', url = getCurrentRelativeUrl() }: UnauthScreenUpdateOptions = {}
  ) => {
    setShowLandingPage(screen === 'preauth')
    if (isAuthenticated) return

    const currentUrl = getCurrentRelativeUrl()
    const currentScreen = getUnauthScreenState(window.history.state)
    if (currentScreen === screen && currentUrl === url) {
      return
    }

    const nextState = buildUnauthHistoryState(screen, window.history.state)
    if (history === 'push') {
      window.history.pushState(nextState, '', url)
      return
    }

    window.history.replaceState(nextState, '', url)
  }, [isAuthenticated])

  const showLoginScreen = useCallback((options: UnauthScreenUpdateOptions = {}) => {
    setUnauthScreen('login', { history: 'push', url: PREAUTH_LOGIN_HASH, ...options })
  }, [setUnauthScreen])

  const showPreAuthScreen = useCallback((options: UnauthScreenUpdateOptions = {}) => {
    setUnauthScreen('preauth', { history: 'replace', url: '/', ...options })
  }, [setUnauthScreen])

  const renderStandaloneScreen = useCallback((
    content: React.ReactNode,
    { includeAudioPlayer = false }: { includeAudioPlayer?: boolean } = {}
  ) => (
    <>
      {content}
      {includeAudioPlayer && audioState.topic && (
        <AudioPlayer
          audioState={audioState}
          onAudioStateChange={setAudioState}
        />
      )}
      <ToastContainer
        toasts={toasts}
        onRemoveToast={removeToast}
      />
    </>
  ), [audioState, removeToast, toasts])

  const exitCanonicalArticleRoute = useCallback(() => {
    canonicalArticleAttemptKeyRef.current = null
    setCanonicalArticleGate(null)
    setDeepLinkResearchId(null)
    setDeepLinkLoading(false)
    setResolveError(null)
    setResolvedArticle(null)
    setArticleToken(null)
    setGlobalArticleOpen(false)

    if (isAuthenticated) {
      setShowLandingPage(false)
      setActiveTab('research')
      window.history.replaceState({ tab: 'research' }, '', getTabUrl('research'))
      return
    }

    showPreAuthScreen({ url: '/' })
  }, [isAuthenticated, showPreAuthScreen])

  const openCanonicalArticleByResearchId = useCallback(async (researchId: number, routeKey: string) => {
    if (canonicalArticleAttemptKeyRef.current === routeKey) return

    canonicalArticleAttemptKeyRef.current = routeKey
    setDeepLinkResearchId(researchId)
    setDeepLinkLoading(true)
    setCanonicalArticleGate(null)
    setResolveError(null)
    setResolvedArticle(null)
    setArticleToken(null)
    setGlobalArticleOpen(false)

    try {
      const publicToken = await mintPublicArticleToken(researchId)
      window.location.replace(`${window.location.origin}/#/article/${publicToken}`)
      return
    } catch (publicError) {
      if (!isAuthenticated) {
        console.warn('Canonical article requires authentication for guests:', publicError)
        setDeepLinkLoading(false)
        setCanonicalArticleGate({
          reason: 'auth_required',
          message: 'Sign in to view this private article.',
        })
        return
      }
    }

    try {
      const ownerToken = await mintArticleToken(researchId)
      window.location.replace(`${window.location.origin}/#/article/${ownerToken}`)
    } catch (ownerError) {
      console.error('Failed to resolve authenticated canonical article link:', ownerError)
      setDeepLinkLoading(false)
      setCanonicalArticleGate({
        reason: 'access_denied',
        message: ownerError instanceof Error ? ownerError.message : 'Unable to open this article.',
      })
    }
  }, [isAuthenticated])

  const handleCanonicalArticleHash = useCallback((hash: string): boolean => {
    const tokenMatch = hash.match(CANONICAL_ARTICLE_TOKEN_HASH_REGEX)
    if (tokenMatch) {
      const token = tokenMatch[1]
      if (isAuthenticated || isPublicArticleToken(token)) {
        canonicalArticleAttemptKeyRef.current = null
        setCanonicalArticleGate(null)
        if (token !== articleToken) {
          setArticleToken(token)
        }
      } else {
        setCanonicalArticleGate({
          reason: 'auth_required',
          message: 'Sign in to view this private article.',
        })
      }
      return true
    }

    const articleIdMatch = hash.match(CANONICAL_ARTICLE_ID_HASH_REGEX)
    if (articleIdMatch) {
      const researchId = parseInt(articleIdMatch[1], 10)
      void openCanonicalArticleByResearchId(
        researchId,
        `hash:${researchId}:${isAuthenticated ? 'auth' : 'guest'}`,
      )
      return true
    }

    return false
  }, [articleToken, isAuthenticated, openCanonicalArticleByResearchId])

  useEffect(() => {
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [handleResize])

  useEffect(() => {
    if (
      isLoading ||
      isAuthenticated ||
      internalPage ||
      deepLinkReportId ||
      shareId ||
      legalPage ||
      showBenchmark ||
      (articleToken && isPublicArticleToken(articleToken))
    ) {
      return
    }

    syncUnauthHistoryState(showLandingPage ? 'preauth' : 'login')
  }, [
    articleToken,
    deepLinkReportId,
    internalPage,
    isAuthenticated,
    isLoading,
    legalPage,
    shareId,
    showBenchmark,
    showLandingPage,
    syncUnauthHistoryState,
  ])

  // Save theme to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('insightatlas-theme', themeMode)
    // Apply theme class to document
    if (themeMode === 'light') {
      document.documentElement.classList.remove('dark')
      document.documentElement.classList.add('light')
    } else {
      document.documentElement.classList.remove('light')
      document.documentElement.classList.add('dark')
    }
  }, [themeMode])
  
  // Track current article view state
  const [globalArticleOpen, setGlobalArticleOpen] = useState(false)
  // Signal for pages to close their article overlays when navigating via browser back
  const [closeArticleSignal, setCloseArticleSignal] = useState(0)
  // Track modal depth and pending navigation when closing via nav
  const modalDepthRef = React.useRef(0)
  const navClosingModalRef = React.useRef(false)
  const pendingTabRef = React.useRef<'research' | 'explore' | 'saved' | 'profile' | null>(null)

  // Email clients wrap universal links in redirect URLs, so we fall back to the
  // custom app scheme only for the canonical email entry routes.
  useEffect(() => {
    const appUrl = resolveEmailAppOpenUrl(window.location.pathname, window.location.search)
    if (!appUrl) {
      return
    }

    const attemptKey = `${window.location.pathname}${window.location.search}`
    if (emailAppOpenAttemptKeyRef.current === attemptKey) {
      return
    }

    emailAppOpenAttemptKeyRef.current = attemptKey
    void tryOpenInApp(appUrl)
  }, [deepLinkReportId])

  // Handle /article/:id path-based deep links (from mobile sharing / email)
  // 
  // Universal Links flow:
  // 1. User taps link: https://www.insightatlas.ai/article/{researchId}
  // 2. If Insight Atlas app is installed and universal links work, iOS opens the app directly
  //    (this JS never runs because iOS intercepts before the page loads)
  // 3. If universal links don't trigger, this page loads and we fall back to web view.
  //
  // IMPORTANT: We do NOT redirect to insightatlas:// custom scheme because that
  // causes the "This site is trying to open another application" permission dialog.
  // The only exception is the email-only fallback handled above.
  useEffect(() => {
    const path = window.location.pathname
    const articlePathMatch = path.match(CANONICAL_ARTICLE_PATH_REGEX)
    
    if (articlePathMatch && !deepLinkLoading) {
      const researchId = parseInt(articlePathMatch[1], 10)
      const routeKey = `path:${researchId}:${isAuthenticated ? 'auth' : 'guest'}`
      const params = new URLSearchParams(window.location.search)
      
      // Clean up URL parameters
      params.delete('source')
      params.delete('openApp')
      const cleanedSearch = params.toString()
      const cleanedUrl = cleanedSearch ? `${path}?${cleanedSearch}` : path
      if (window.location.search !== (cleanedSearch ? `?${cleanedSearch}` : '')) {
        window.history.replaceState({}, '', cleanedUrl)
      }

      if (canonicalArticleAttemptKeyRef.current !== routeKey) {
        void openCanonicalArticleByResearchId(researchId, routeKey)
      }
    }
  }, [deepLinkLoading, isAuthenticated, openCanonicalArticleByResearchId])

  // /report/:reportId is now handled synchronously via deepLinkReportId state
  // and renders ReportViewPage directly — no redirect needed.

  // Check for share URL and canonical/page article hash routes; handle browser navigation
  useEffect(() => {
    // Wait for auth to be determined before processing article URLs
    if (isLoading) return

    const normalizeUnauthenticatedRoute = (hash: string) => {
      if (hash === '/login') {
        setShowLandingPage(false)
        return
      }

      if (isAppTabHash(hash)) {
        window.history.replaceState(
          buildUnauthHistoryState('preauth', window.history.state),
          '',
          '/'
        )
      }

      setShowLandingPage(true)
    }

    // Handle browser back/forward buttons
    const handlePopState = (event: PopStateEvent) => {
      // If we have modal entries on the stack, treat this as closing the top-most modal
      if (modalDepthRef.current > 0) {
        modalDepthRef.current = Math.max(0, modalDepthRef.current - 1)
        // Close any overlay: page-level or profile-level
        if (globalArticleOpen) {
          setGlobalArticleOpen(false)
          window.dispatchEvent(new Event('insightatlas-close-article'))
        }
        if (selectedProfileResearch) {
          setSelectedProfileResearch(null)
          setProfileIntelligenceReportData(null)
        }
        // If this pop was initiated due to a tab change while a modal was up, complete the tab change now
        if (navClosingModalRef.current && pendingTabRef.current) {
          const target = pendingTabRef.current
          navClosingModalRef.current = false
          pendingTabRef.current = null
          setActiveTab(target)
          window.history.pushState({ tab: target }, '', getTabUrl(target))
        }
        return
      }
      // No tracked modal depth, but profile article may still be open (e.g., opened before tracking existed)
      if (selectedProfileResearch) {
        setSelectedProfileResearch(null)
        setProfileIntelligenceReportData(null)
        return
      }
      const internalRoute = getInternalPageRoute(window.location.pathname)
      if (internalRoute) {
        setInternalPage(internalRoute)
        return
      }
      if (internalPage) {
        setInternalPage(null)
      }
      // No modal depth: navigate to tab by state (if provided)
      const reportMatch = window.location.pathname.match(CANONICAL_REPORT_PATH_REGEX)
      if (reportMatch) {
        setDeepLinkReportId(reportMatch[1])
        return
      }

      if (deepLinkReportId) {
        setDeepLinkReportId(null)
      }

      if (!isAuthenticated) {
        const currentHash = window.location.hash.slice(1)
        if (currentHash === '/terms' || currentHash === '/privacy') {
          return
        }

        if (handleCanonicalArticleHash(currentHash)) {
          return
        }

        normalizeUnauthenticatedRoute(currentHash)
        return
      }

      const pathTab = getPathTab(window.location.pathname)
      if (pathTab) {
        setActiveTab(pathTab)
        return
      }

      if (event.state && event.state.tab) {
        setActiveTab(event.state.tab)
      }
    }
    
    window.addEventListener('popstate', handlePopState)
    const onHashChange = () => {
      const internalRoute = getInternalPageRoute(window.location.pathname)
      if (internalRoute) {
        setInternalPage(internalRoute)
        return
      }
      if (internalPage) {
        setInternalPage(null)
      }
      const h = window.location.hash.slice(1)

      if (h === '/terms' || h === '/privacy') {
        setLegalPage(h === '/terms' ? 'terms' : 'privacy')
        return
      }
      if (legalPage) setLegalPage(null)

      if (handleCanonicalArticleHash(h)) {
        return
      }

      setCanonicalArticleGate(null)
      canonicalArticleAttemptKeyRef.current = null
      if (articleToken) {
        setArticleToken(null)
        setResolvedArticle(null)
        setGlobalArticleOpen(false)
      }

      {
        // Match both numeric IDs (e.g., 123) and intelligence report IDs (e.g., ia-report_abc123)
        const pageMatch = h.match(/^(research|explore|saved|profile)\/article\/([\w-]+)$/)
        if (pageMatch) {
          // Only process page article routes if authenticated
          if (isAuthenticated) {
            // Ensure active tab aligns with hash; page component handles overlay
            const tab = pageMatch[1] as 'research' | 'explore' | 'saved' | 'profile'
            if (activeTab !== tab) setActiveTab(tab)
          } else {
            // Not authenticated - save URL for post-login redirect
            sessionStorage.setItem('insightatlas-return-url', window.location.hash)
            showLoginScreen({ history: 'replace' })
          }
          return
        }
      }

      if (!isAuthenticated) {
        normalizeUnauthenticatedRoute(h)
        return
      }

      if (isAppTabHash(h) && activeTab !== h) {
        setActiveTab(h)
      }
    }

    window.addEventListener('hashchange', onHashChange)

    const cleanup = () => {
      window.removeEventListener('popstate', handlePopState)
      window.removeEventListener('hashchange', onHashChange)
    }

    const path = window.location.pathname
    const internalRoute = getInternalPageRoute(path)

    if (internalRoute) {
      if (internalPage !== internalRoute) {
        setInternalPage(internalRoute)
      }
      if (!isAuthenticated) {
        sessionStorage.setItem('insightatlas-return-url', getCurrentRelativeUrl())
      }
      return cleanup
    }

    if (internalPage) {
      setInternalPage(null)
    }

    const reportPathMatch = path.match(CANONICAL_REPORT_PATH_REGEX)
    if (reportPathMatch) {
      const reportId = reportPathMatch[1]
      if (deepLinkReportId !== reportId) {
        setDeepLinkReportId(reportId)
      }
      return cleanup
    }

    if (deepLinkReportId) {
      setDeepLinkReportId(null)
    }

    // Skip if we're handling a deep link /article/:id path (handled by synchronous state init)
    if (path.match(/^\/article\/\d+$/)) {
      return cleanup
    }

    const shareMatch = path.match(/^\/s\/([a-zA-Z0-9]+)$/)
    if (shareMatch) {
      setShareId(shareMatch[1])
      return cleanup
    }

    const hash = window.location.hash.slice(1)

    // Legal pages don't need further route processing
    if (hash === '/terms' || hash === '/privacy') return cleanup

    if (handleCanonicalArticleHash(hash)) {
      return cleanup
    }

    // Page-level overlay routes like #research/article/<id>, #explore/article/<id>, etc.
    // Match both numeric IDs (e.g., 123) and intelligence report IDs (e.g., ia-report_abc123)
    const pageArticleMatch = hash.match(/^(research|explore|saved|profile)\/article\/([\w-]+)$/)
    if (pageArticleMatch) {
      if (isAuthenticated) {
        const tab = pageArticleMatch[1] as 'research' | 'explore' | 'saved' | 'profile'
        setActiveTab(tab)
      } else {
        sessionStorage.setItem('insightatlas-return-url', window.location.hash)
        showLoginScreen({ history: 'replace' })
      }
      return cleanup
    }

    if (!isAuthenticated) {
      normalizeUnauthenticatedRoute(hash)
      return cleanup
    }

    const pathTab = getPathTab(path)
    if (pathTab) {
      if (activeTab !== pathTab) {
        setActiveTab(pathTab)
      }
      return cleanup
    }

    // Handle initial hash navigation for plain tabs
    if (hash && isAppTabHash(hash)) {
      setActiveTab(hash)
      return cleanup
    }

    // Default to explore if no recognized hash
    setActiveTab('explore')
    window.history.replaceState(
      { ...getHistoryStateObject(window.history.state), tab: 'explore' },
      '',
      getTabUrl('explore')
    )

    return cleanup
  }, [globalArticleOpen, articleToken, activeTab, isLoading, isAuthenticated, deepLinkReportId, internalPage]) // eslint-disable-line react-hooks/exhaustive-deps

  // Resolve article token when present
  useEffect(() => {
    const doResolve = async () => {
      if (!articleToken) return
      setResolvingArticle(true)
      setResolveError(null)
      try {
        const data = await resolveArticleByToken(articleToken)
        setResolvedArticle(data)
        setGlobalArticleOpen(true)
      } catch (e) {
        console.error('Failed to resolve article token', e)
        const message = e instanceof Error ? e.message : 'Unable to load this article.'
        setResolveError(message)
        setGlobalArticleOpen(true)
      } finally {
        setResolvingArticle(false)
      }
    }
    doResolve()
  }, [articleToken])

  // Reset state when authentication changes
  useEffect(() => {
    if (!isAuthenticated && !isLoading) {
      const canonicalArticleRouteActive = isCanonicalArticleLocation(window.location.pathname, window.location.hash.slice(1))
      // Reset to explore tab when logged out
      setSelectedProfileResearch(null)
      setProfileIntelligenceReportData(null)
      setProfileUserEmail(undefined)
      // Only clear article state if it's NOT a public token (public articles can be viewed without auth)
      if (!isPublicArticleToken(articleToken)) {
        setGlobalArticleOpen(false)
        setArticleToken(null)
        if (!canonicalArticleRouteActive) {
          setActiveTab('explore')
        }
      }
    } else if (isAuthenticated && !isLoading) {
      const canonicalArticleRouteActive = isCanonicalArticleLocation(window.location.pathname, window.location.hash.slice(1))
      // If viewing a shared article or deep-linked report, stay on it
      if (shareId || deepLinkReportId || canonicalArticleRouteActive || canonicalArticleGate) {
        setSelectedProfileResearch(null)
        setProfileIntelligenceReportData(null)
      } else if (articleToken && isPublicArticleToken(articleToken)) {
        // Keep viewing the public article after login
        setSelectedProfileResearch(null)
        setProfileIntelligenceReportData(null)
      } else {
        if (internalPage) {
          setGlobalArticleOpen(false)
          setSelectedProfileResearch(null)
          setProfileIntelligenceReportData(null)
        } else {
        // Check for a saved return URL from pre-login redirect
          const returnUrl = sessionStorage.getItem('insightatlas-return-url')
          sessionStorage.removeItem('insightatlas-return-url')

          if (returnUrl) {
            window.history.replaceState({}, '', returnUrl)
            setActiveTab(getTabFromRelativeUrl(returnUrl) ?? 'explore')
          } else {
            setActiveTab('research')
            window.history.replaceState({ tab: 'research' }, '', getTabUrl('research'))
          }
          setGlobalArticleOpen(false)
          setSelectedProfileResearch(null)
          setProfileIntelligenceReportData(null)
          
          // Check for pending research from landing page
          const pending = getPendingResearch()
          if (pending) {
            // Store pending research for ResearchPage to pick up
            sessionStorage.setItem('autoStartResearch', JSON.stringify(pending))
            clearPendingResearch()
            // Dispatch event so ResearchPage can pick it up even if already mounted
            window.dispatchEvent(new CustomEvent('pendingResearchReady', { detail: pending }))
          }
        }
      }
      
      // Trigger session recovery when authenticated
      import('./lib/api').then(({ triggerSessionRecovery }) => {
        if (typeof triggerSessionRecovery !== 'function') {
          return
        }

        const recoveryRequest = triggerSessionRecovery()
        if (!recoveryRequest || typeof (recoveryRequest as Promise<any>).then !== 'function') {
          return
        }

        recoveryRequest.then(result => {
          if (result.recovered > 0) {
            // console.log(`Recovered ${result.recovered} failed research tasks`)
            // Toast notification will be shown when research list refreshes
          }
        }).catch(err => {
          console.error('Session recovery check failed:', err)
        })
      })
    }
  }, [isAuthenticated, isLoading, articleToken, canonicalArticleGate, shareId, deepLinkReportId, internalPage])

  // Debug selectedProfileResearch changes

  // Global play event listener for pages that don't have direct access to setAudioState
  useEffect(() => {
    const handler = (e: any) => {
      try {
        const { topic, audio_url } = e.detail || {}
        if (!topic || !audio_url) return
        const newTopic = { ...topic, audio_url }
        setAudioState({
          isPlaying: true,
          currentTime: 0,
          duration: 0,
          topic: newTopic
        })
      } catch {}
    }
    window.addEventListener('insightatlas-play-podcast', handler as any)
    return () => window.removeEventListener('insightatlas-play-podcast', handler as any)
  }, [])

  // When an article opens, push a history state so browser Back will close it first
  useEffect(() => {
    if (globalArticleOpen) {
      const tabForState = activeTab
      window.history.pushState({ tab: tabForState, modal: 'article' }, '')
      modalDepthRef.current += 1
    } else {
      // No-op when closing; popstate handles stack via back()
    }
  }, [globalArticleOpen, activeTab])

  // When profile inline article opens/closes, sync URL hash so Back closes it first
  useEffect(() => {
    const currentHash = window.location.hash
    if (selectedProfileResearch) {
      const targetHash = `#profile/article/${selectedProfileResearch.id}`
      if (currentHash !== targetHash) {
        window.history.pushState({ tab: 'profile', modal: 'profile-article' }, '', targetHash)
        modalDepthRef.current += 1
      }
    } else {
      if (currentHash.startsWith('#profile/article/')) {
        window.history.replaceState({ tab: 'profile' }, '', '#profile')
      }
    }
  }, [selectedProfileResearch])

  // Close profile article when navigating back to #profile via hashchange
  useEffect(() => {
    const onHashChange = () => {
      if (window.location.hash === '#profile' && selectedProfileResearch) {
        setSelectedProfileResearch(null)
        setProfileIntelligenceReportData(null)
      }
    }
    window.addEventListener('hashchange', onHashChange)
    return () => window.removeEventListener('hashchange', onHashChange)
  }, [selectedProfileResearch])
  
  // Handle tab change with browser history support
  const handleTabChange = useCallback((tab: AppTab, skipHistory?: boolean) => {
    // Track tab change and SPA page view with Amplitude
    if (activeTab !== tab) {
      analytics.trackTabChanged({ from_tab: activeTab, to_tab: tab })
      analytics.trackPageViewed({ page: getTabUrl(tab) })
    }

    // Always signal pages to close any open overlay
    window.dispatchEvent(new Event('insightatlas-close-article'))

    // Close any open article and navigate immediately (no double-click needed)
    if (globalArticleOpen) {
      setGlobalArticleOpen(false)
      // If we have modal history, we need to clean it up
      if (modalDepthRef.current > 0) {
        modalDepthRef.current = 0  // Reset modal depth
      }
      // Continue to navigation below (don't return early)
    }
    
    // If profile inline article is open, close it
    if (selectedProfileResearch) {
      setSelectedProfileResearch(null)
      setProfileIntelligenceReportData(null)
      if (modalDepthRef.current > 0) {
        modalDepthRef.current = 0  // Reset modal depth
      }
      // Continue to navigation below (don't return early)
    }
    
    // Navigate to the target tab
    setActiveTab(tab)
    
    // Update browser history (unless we're responding to a history event)
    if (!skipHistory) {
      window.history.pushState({ tab }, '', getTabUrl(tab))
    }
    
    // Clear profile user email when navigating away from profile
    if (tab !== 'profile') {
      setProfileUserEmail(undefined)
    }
    
    // Scroll to top - use all methods for better compatibility
    window.scrollTo(0, 0)
    document.documentElement.scrollTop = 0
    document.body.scrollTop = 0
    
    // For mobile, also try scrolling the main content container
    const mainContent = document.querySelector('#root')
    if (mainContent) {
      mainContent.scrollTop = 0
    }
  }, [globalArticleOpen, selectedProfileResearch])

  // Handle navigation to user profile (ensure article overlay closes first)
  const navigateToUserProfile = useCallback((userEmail: string) => {
    console.log('navigateToUserProfile called with:', userEmail)
    
    // Close any open articles first
    if (globalArticleOpen) {
      setGlobalArticleOpen(false)
      window.dispatchEvent(new Event('insightatlas-close-article'))
      // Reset modal depth
      modalDepthRef.current = 0
    }
    
    // Close profile article if open
    if (selectedProfileResearch) {
      setSelectedProfileResearch(null)
      setProfileIntelligenceReportData(null)
      modalDepthRef.current = 0
    }
    
    // Set the profile user email and navigate to profile
    setProfileUserEmail(userEmail)
    setActiveTab('profile')
    window.history.pushState({ tab: 'profile', userEmail }, '', '#profile')
    
    // Scroll to top when navigating to profile
    window.scrollTo(0, 0)
  }, [globalArticleOpen, selectedProfileResearch])

  // Handler for profile research click - fetches intelligence report data if needed
  const handleProfileResearchClick = useCallback(async (research: any) => {
    const isIntelligenceReport =
      research.is_intelligence_report ||
      research.isIntelligenceReport ||
      (typeof research.id === 'string' && research.id.startsWith('ia-'))

    if (!isIntelligenceReport || research.status !== 'completed') {
      return
    }

    const reportId =
      typeof research.id === 'string' && research.id.startsWith('ia-')
        ? research.id.replace('ia-', '')
        : (research.intelligence_report_id || research.intelligenceReportId || research.report_id)

    if (!reportId) {
      addToast({
        type: 'error',
        title: 'Unable to Load Report',
        description: 'This report is missing its intelligence report identifier.',
      })
      return
    }

    const pendingProfileResearch: IntelligenceReportStudy = {
      ...research,
      id: String(research.id || `ia-${reportId}`),
      query: research.query || research.title || '',
      title: research.title || research.query || 'Intelligence Report',
      createdAt: research.createdAt || research.created_at,
      completedAt: research.completedAt || research.completed_at,
      isIntelligenceReport: true,
      intelligenceReportId: reportId,
      researchId: research.researchId ?? research.research_id,
      isSaved: research.isSaved ?? research.is_saved,
      isPublic: research.isPublic ?? research.is_public,
      author: research.author || (research.author_name ? {
        name: research.author_name,
        email: research.author_email,
        avatar_url: research.author_avatar,
      } : undefined),
      image_url: research.image_url,
      podcast_status: research.podcast_status,
      podcast_audio_ready: research.podcast_audio_ready,
      podcast_duration_seconds: research.podcast_duration_seconds,
      podcast_error_message: research.podcast_error_message,
    }

    setSelectedProfileResearch(pendingProfileResearch)
    setProfileIntelligenceReportData(null)
    setIsLoadingProfileReport(true)

    try {
      const response = await fetch(`${API_BASE_URL}/api/intelligence/report/${reportId}`, {
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader(),
        },
      })

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`)
      }

      const normalizedReportData = normalizeIntelligenceReportData(await response.json())
      if (normalizedReportData.report_type !== 'intelligence' || !normalizedReportData.cig || !normalizedReportData.sib) {
        throw new Error('Incomplete intelligence report payload')
      }

      setProfileIntelligenceReportData(normalizedReportData)
      setSelectedProfileResearch(buildIntelligenceReportStudy(pendingProfileResearch, normalizedReportData))
    } catch (error) {
      console.error('[Profile] Error fetching intelligence report:', error)
      setSelectedProfileResearch(null)
      setProfileIntelligenceReportData(null)
      addToast({
        type: 'error',
        title: 'Unable to Load Report',
        description: 'Please try opening this report again in a moment.',
      })
    } finally {
      setIsLoadingProfileReport(false)
    }
  }, [addToast])

  // Memoize profile page only (it changes based on selectedProfileResearch)
  const profilePage = useMemo(() => {
    return (
      <>
        <ProfilePage 
          viewMode={viewMode}
          onShowToast={addToast}
          themeMode={themeMode}
          onThemeChange={setThemeMode}
          userEmail={profileUserEmail}
          onBillingStatusSync={handleBillingStatusSync}
          onNavigateToResearch={() => handleTabChange('research')}
          onBack={profileUserEmail ? () => {
            setProfileUserEmail(undefined)
            setActiveTab('explore')
          } : undefined}
          onResearchClick={handleProfileResearchClick}
        />

        {isLoadingProfileReport && selectedProfileResearch && (
          <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl flex flex-col items-center">
              <LoadingAnimation size={48} />
              <p className="mt-4 text-gray-700 dark:text-gray-300">Loading research content...</p>
            </div>
          </div>
        )}

        {selectedProfileResearch && profileIntelligenceReportData && (
          <div className="fixed inset-0 z-40 bg-gray-50 dark:bg-gray-900">
            <div className={`h-full ${viewMode === 'mobile' ? '' : 'pl-24'} overflow-y-auto`}>
              <IntelligenceReportCockpit
                reportData={profileIntelligenceReportData}
                title={selectedProfileResearch.title || selectedProfileResearch.query || 'Intelligence Report'}
                study={selectedProfileResearch}
                onShowToast={addToast}
                onBack={() => {
                  setSelectedProfileResearch(null)
                  setProfileIntelligenceReportData(null)
                }}
                viewMode={viewMode}
                hasFloatingAudio={Boolean(audioState.topic)}
                onNavigateToProfile={(userEmail) => {
                  setSelectedProfileResearch(null)
                  setProfileIntelligenceReportData(null)
                  if (userEmail) {
                    setProfileUserEmail(userEmail)
                    setActiveTab('profile')
                  }
                }}
              />
            </div>
          </div>
        )}
      </>
    )
  }, [viewMode, themeMode, addToast, profileUserEmail, selectedProfileResearch, profileIntelligenceReportData, isLoadingProfileReport, handleBillingStatusSync, handleProfileResearchClick, handleTabChange, audioState.topic])

  // Show loading screen while resolving deep link /article/:id path
  if (deepLinkLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <LoadingAnimation size={48} />
          <p className="text-gray-600 dark:text-gray-400 mt-4 font-medium">Opening article...</p>
        </div>
      </div>
    )
  }

  // Show offline page when internet connection is lost
  if (!isOnline) {
    return (
      <OfflinePage 
        onRetry={checkConnection} 
        isChecking={isChecking}
      />
    )
  }

  // Show shared article page if we have a share ID
  if (shareId) {
    return renderStandaloneScreen(
      <SharedArticlePage
        isDarkMode={themeMode === 'dark'}
        onShowToast={addToast}
      />
    )
  }

  // Show report view page for /report/{reportId} deep links
  if (deepLinkReportId) {
    return renderStandaloneScreen(
      <ReportViewPage
        reportId={deepLinkReportId}
        isDarkMode={themeMode === 'dark'}
        onShowToast={addToast}
        viewMode={viewMode}
        hasFloatingAudio={Boolean(audioState.topic)}
      />
      ,
      { includeAudioPlayer: true }
    )
  }

  // Show loading screen while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-gray-300 border-t-gray-900 dark:border-gray-700 dark:border-t-white rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    )
  }

  if (internalPage === 'executive') {
    if (!isAuthenticated) {
      return renderStandaloneScreen(
        <LoginPage
          viewMode={viewMode}
          onShowToast={addToast}
        />
      )
    }

    return (
      <React.Suspense fallback={<div className="bg-gray-950 min-h-screen flex items-center justify-center text-white">Loading executive analytics...</div>}>
        <ExecutiveAnalyticsPage userEmail={user?.email || null} />
      </React.Suspense>
    )
  }

  // PUBLIC ARTICLE VIEWER: Show public articles without requiring authentication
  // This must come BEFORE the landing/login page checks
  if (articleToken && isPublicArticleToken(articleToken) && !isAuthenticated) {
    return (
      <div className={`min-h-screen transition-colors duration-300 overflow-x-hidden ${
        themeMode === 'dark' ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'
      }`}>
        <div className="fixed inset-0 z-[60] bg-white dark:bg-gray-900">
          <div id="article-scroll-container" className="h-full overflow-y-auto">
            {resolvingArticle && (
              <div className="w-full h-full flex flex-col items-center justify-center space-y-4">
                <LoadingAnimation size={48} />
                <p className="text-gray-600 dark:text-gray-400 font-medium">Loading article...</p>
              </div>
            )}
            {!resolvingArticle && resolveError && (
              <div className="w-full h-full flex items-center justify-center px-4">
                <div className="max-w-md w-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-xl p-6 text-center">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Unable to Load Article</h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                    {resolveError.includes('Invalid token') || resolveError.includes('expired')
                      ? 'This link may be invalid or expired. Please ask the sender to share it again.'
                      : resolveError}
                  </p>
                  <button
                    onClick={() => {
                      setArticleToken(null)
                      setResolvedArticle(null)
                      setResolveError(null)
                      setGlobalArticleOpen(false)
                      showPreAuthScreen({ url: '/' })
                    }}
                    className="inline-flex items-center justify-center px-5 py-2.5 rounded-xl bg-black dark:bg-white text-white dark:text-black text-sm font-medium hover:bg-gray-900 dark:hover:bg-gray-100 transition-colors"
                  >
                    Go to Home
                  </button>
                </div>
              </div>
            )}
            {!resolvingArticle && !resolveError && resolvedArticle && (
              <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <button
                  onClick={() => {
                    setArticleToken(null)
                    setResolvedArticle(null)
                    setGlobalArticleOpen(false)
                    showPreAuthScreen({ url: '/' })
                  }}
                  className="mb-6 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 flex items-center gap-1"
                >
                  &larr; Back
                </button>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">{resolvedArticle.title}</h1>
                {resolvedArticle.summary && <p className="text-gray-600 dark:text-gray-400 mb-6">{resolvedArticle.summary}</p>}
                {resolvedArticle.report_text && (
                  <div className="prose dark:prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: resolvedArticle.report_text.replace(/\n/g, '<br/>') }} />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  if (canonicalArticleGate && (canonicalArticleGate.reason === 'access_denied' || (!isAuthenticated && showLandingPage))) {
    const gateTitle = canonicalArticleGate.reason === 'auth_required' ? 'Private Article' : 'Unable to Open Article'
    const gateMessage = canonicalArticleGate.reason === 'auth_required'
      ? canonicalArticleGate.message
      : canonicalArticleGate.message || 'This article is unavailable.'

    return renderStandaloneScreen(
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a] p-4">
        <div className="bg-white/[0.03] backdrop-blur-xl rounded-3xl border border-white/10 p-8 shadow-2xl max-w-md w-full">
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-4">
              <img src="/logo192.png" alt="Insight Atlas" className="w-full h-full rounded-2xl shadow-lg" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">{gateTitle}</h2>
            <p className="text-white/50 text-sm">{gateMessage}</p>
          </div>
          <div className="space-y-3">
            {canonicalArticleGate.reason === 'auth_required' && !isAuthenticated && (
              <button
                onClick={() => showLoginScreen()}
                className="w-full inline-flex items-center justify-center rounded-2xl bg-white text-black px-5 py-3 text-sm font-semibold hover:bg-gray-100 transition-colors"
              >
                Sign In to View
              </button>
            )}
            <button
              onClick={exitCanonicalArticleRoute}
              className="w-full inline-flex items-center justify-center rounded-2xl border border-white/15 text-white px-5 py-3 text-sm font-medium hover:bg-white/5 transition-colors"
            >
              {isAuthenticated ? 'Go to Research' : 'Go to Home'}
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Dev-only benchmark page (no auth required)
  if (showBenchmark) {
    return (
      <React.Suspense fallback={<div className="bg-gray-950 min-h-screen flex items-center justify-center text-white">Loading benchmark...</div>}>
        <BenchmarkPage />
      </React.Suspense>
    )
  }

  // Standalone legal pages (no auth required)
  if (legalPage) {
    return <LegalPage type={legalPage} />
  }

  // Show landing page first for new visitors.
  if (showLandingPage && !isAuthenticated) {
    return renderStandaloneScreen(
      <LandingPage
        viewMode={viewMode}
        onLogin={() => showLoginScreen()}
        onGetStarted={() => showLoginScreen()}
      />,
      { includeAudioPlayer: true }
    )
  }

  // Show login page if not authenticated (after landing page)
  if (!isAuthenticated) {
    return renderStandaloneScreen(
      <LoginPage 
        viewMode={viewMode}
        onShowToast={addToast}
      />
    )
  }

  return (
    <div className={`min-h-screen transition-colors duration-300 overflow-x-hidden ${
      themeMode === 'dark' 
        ? 'bg-gray-900 text-white' 
        : 'bg-gray-50 text-gray-900'
    }`}>
      {/* User Avatar - Fixed position */}
      {viewMode !== 'mobile' && (
        <div className="fixed top-4 right-4 z-[70]">
          <UserAvatar 
            viewMode={viewMode}
            onShowToast={addToast}
          />
        </div>
      )}

      {/* Main content area with proper spacing for left navigation */}
      <div className={`min-h-screen ${
        viewMode === 'mobile' 
          ? 'pb-16' 
          : 'pl-24' // Space for collapsed left nav
      } ${audioState.topic ? 'pb-20' : ''}`}>
        {/* Global Article Viewer (hash-based) */}
        {articleToken && (
          <div className="fixed inset-0 z-[60] bg-white dark:bg-gray-900">
            <div id="article-scroll-container" className={`h-full ${viewMode === 'mobile' ? '' : 'pl-24'} overflow-y-auto`}>
            {resolvingArticle && (
              <div className="w-full h-full flex flex-col items-center justify-center space-y-4">
                <LoadingAnimation size={48} />
                <p className="text-gray-600 dark:text-gray-400 font-medium">Loading article...</p>
              </div>
            )}
            {!resolvingArticle && resolveError && (
              <div className="w-full h-full flex items-center justify-center px-4">
                <div className="max-w-md w-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-xl p-6 text-center">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Unable to Load Article</h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                    {resolveError.includes('Invalid token') || resolveError.includes('expired')
                      ? 'This link may be invalid or expired. Please ask the sender to share it again.'
                      : resolveError}
                  </p>
                  <button
                    onClick={() => {
                      setGlobalArticleOpen(false)
                      setArticleToken(null)
                      setResolvedArticle(null)
                      setResolveError(null)
                      if (window.location.hash.startsWith('#/article/')) {
                        window.history.back()
                      }
                    }}
                    className="inline-flex items-center justify-center px-5 py-2.5 rounded-xl bg-black dark:bg-white text-white dark:text-black text-sm font-medium hover:bg-gray-900 dark:hover:bg-gray-100 transition-colors"
                  >
                    Go back
                  </button>
                </div>
              </div>
            )}
            {!resolvingArticle && !resolveError && resolvedArticle && (
              <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <button
                  onClick={() => { setGlobalArticleOpen(false); setArticleToken(null); setResolvedArticle(null); if (window.location.hash.startsWith('#/article/')) { window.history.back() } }}
                  className="mb-6 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 flex items-center gap-1"
                >
                  &larr; Back
                </button>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">{resolvedArticle.title}</h1>
                {resolvedArticle.summary && <p className="text-gray-600 dark:text-gray-400 mb-6">{resolvedArticle.summary}</p>}
                {resolvedArticle.report_text && (
                  <div className="prose dark:prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: resolvedArticle.report_text.replace(/\n/g, '<br/>') }} />
                )}
              </div>
            )}
            </div>
          </div>
        )}
        {/* Keep pages mounted to preserve state; hide inactive via display */}
        <div style={{ display: activeTab === 'research' ? 'block' : 'none' }}>
          <ResearchPage 
            viewMode={viewMode}
            onShowToast={addToast}
            hasFloatingAudio={Boolean(audioState.topic)}
            onModalToggle={() => {}} 
            onNavigateToProfile={navigateToUserProfile}
            onArticleOpenChange={setGlobalArticleOpen}
          />
        </div>
        
        <div style={{ display: activeTab === 'explore' ? 'block' : 'none' }}>
          <HomePage 
            viewMode={viewMode} 
            isDarkMode={themeMode === 'dark'}
            onToggleDarkMode={toggleTheme}
            onAudioStateChange={setAudioState}
            onShowToast={addToast}
            onArticleOpen={setGlobalArticleOpen}
            onTabChange={handleTabChange}
            onNavigateToProfile={navigateToUserProfile}
          />
        </div>
        
        <div style={{ display: activeTab === 'saved' ? 'block' : 'none' }}>
          <SavedPage 
            viewMode={viewMode}
            onAudioStateChange={setAudioState}
            onShowToast={addToast}
            onNavigateToProfile={navigateToUserProfile}
            onArticleOpenChange={setGlobalArticleOpen}
          />
        </div>
        
        <div style={{ display: activeTab === 'profile' ? 'block' : 'none' }}>
          {profilePage}
        </div>

        {viewMode !== 'mobile' && <Footer />}
      </div>

      {/* Navigation - always visible, stays on article pages for easy navigation */}
      <BottomNavigation 
        activeTab={activeTab}
        onTabChange={(tab) => {
          // If user taps Profile in main nav, show own profile by default
          if (tab === 'profile') {
            setProfileUserEmail(undefined)
          }
          handleTabChange(tab)
        }}
        viewMode={viewMode}
        themeMode={themeMode}
        onThemeChange={setThemeMode}
        // Keep navigation visible on article pages for easy navigation
        isVisible={isNavVisible}
        hasBillingIssue={hasBillingIssue}
      />
      
      {/* Audio Player */}
      {audioState.topic && (
        <AudioPlayer 
          audioState={audioState}
          onAudioStateChange={setAudioState}
        />
      )}

      {/* Toast notifications */}
      <ToastContainer 
        toasts={toasts}
        onRemoveToast={removeToast}
      />
    </div>
  )
}

// Main App Component with AuthProvider
function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  )
}

export default App 